/**
 * Pure initialization utilities for coverage state.
 * These functions have no store dependencies and can be safely imported anywhere.
 */
import { CoverageProduct, type Coverage } from 'dto/marketableProduct';
import type { BackgroundInfoForm } from 'individual/types/backgroundInfoForm';
import type { IndividualLifeForm } from 'individual/types/initialState';
import { withinAgeRange } from 'utils';

/**
 * Represents a selected coverage with primitive sum insured and deductible values.
 * Store only primitives - Premium objects are derived via selectors.
 */
export interface CoverageSelection {
  sumInsured: number;
  deductibleDays: number | null;
}

/**
 * Determines initial coverage selections based on authentication and calculator input state.
 *
 * @param marketableCoverages - Available coverages from marketable product
 * @param calculatorInput - Optional calculator input from authenticated session
 * @returns Object with coverageSelections (Record) and selectedCoverages (array)
 */
export function getInitialCoverageSelections(
  marketableCoverages: Coverage[],
  calculatorInput: IndividualLifeForm | null
): {
  coverageSelections: Record<CoverageProduct, CoverageSelection | undefined>;
  selectedCoverages: CoverageProduct[];
} {
  let selectedCoverages: CoverageProduct[];

  if (calculatorInput) {
    // AUTHENTICATED PATH: Use calculatorInput.coverages (actual selections from server)
    // Mandatory coverages + coverages present in calculatorInput
    selectedCoverages = marketableCoverages
      .filter(
        (coverage) =>
          coverage.mandatory ||
          coverage.coverageProduct in calculatorInput.coverages
      )
      .map((coverage) => coverage.coverageProduct);
  } else {
    selectedCoverages = marketableCoverages
      .filter((coverage) => coverage.mandatory || coverage.preSelected)
      .map((coverage) => coverage.coverageProduct);
  }

  // Build coverageSelections Record from selectedCoverages array
  const initialCoverageSelections: Record<
    CoverageProduct,
    CoverageSelection | undefined
  > = {
    [CoverageProduct.LIFE]: undefined,
    [CoverageProduct.CRITICAL_ILLNESS]: undefined,
    [CoverageProduct.PERMANENT_DISABILITY]: undefined,
    [CoverageProduct.TEMPORARY_DISABILITY]: undefined,
  };

  const coverageSelections = Object.values(CoverageProduct).reduce<
    Record<CoverageProduct, CoverageSelection | undefined>
  >((acc, coverageProduct) => {
    if (selectedCoverages.includes(coverageProduct)) {
      const calculatorSelection = calculatorInput?.coverages[coverageProduct];

      if (calculatorSelection) {
        // Use calculator input values
        acc[coverageProduct] = {
          sumInsured: calculatorSelection.sumInsured,
          deductibleDays: calculatorSelection.deductibleDays ?? null,
        };
      } else {
        // Will be populated when coveragePremiums are loaded
        acc[coverageProduct] = undefined;
      }
    } else {
      acc[coverageProduct] = undefined;
    }
    return acc;
  }, initialCoverageSelections);

  return { coverageSelections, selectedCoverages };
}

/**
 * Creates an empty coverage selections record with all products set to undefined.
 */
export function createEmptyCoverageSelections(): Record<
  CoverageProduct,
  CoverageSelection | undefined
> {
  return {
    [CoverageProduct.LIFE]: undefined,
    [CoverageProduct.CRITICAL_ILLNESS]: undefined,
    [CoverageProduct.PERMANENT_DISABILITY]: undefined,
    [CoverageProduct.TEMPORARY_DISABILITY]: undefined,
  };
}

/**
 * Pure function to filter available coverages based on age and working status.
 * This is the core filtering logic without store dependencies.
 *
 * @param coverages - All coverages from marketable product
 * @param backgroundInfoForm - User's background info form data
 * @returns Filtered and sorted coverages available to the user
 */
export function filterAvailableCoverages(
  coverages: Coverage[] | undefined,
  backgroundInfoForm: BackgroundInfoForm | undefined
): Coverage[] | undefined {
  if (!coverages) {
    return undefined;
  }

  return [...coverages]
    .filter((coverage) =>
      withinAgeRange(coverage, backgroundInfoForm?.dateOfBirth ?? '')
    )
    .filter(
      (coverage) =>
        !coverage.requiredWorkingStatus ||
        coverage.requiredWorkingStatus === backgroundInfoForm?.workingStatus
    )
    .sort((a, b) => a.order - b.order);
}
